import { QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App.tsx';
import { createOpsQueryClient } from './api/queryClient.ts';
import { I18nProvider } from './i18n/index.ts';
import './styles.css';

/**
 * Browser entry. Vite compiles this to `dist/assets/main.*.js` and
 * `index.html` pulls it as a module. Keep this file tiny — all app
 * composition lives in `App.tsx`.
 *
 * A single `QueryClient` instance wraps the entire app; tests mount
 * their own via `<QueryClientProvider>` inside `MemoryRouter`.
 *
 * `<I18nProvider>` owns the locale (`ops.locale` in localStorage) and
 * exposes `useT()` + `useLocale()` to every page. It also syncs
 * `<html lang>`, `document.title` and `<meta name="description">` on
 * locale flip so the tab title + screen-reader announcements stay in
 * step with the UI copy.
 */
const rootEl = document.getElementById('root');
if (rootEl === null) {
  throw new Error('ops-console: #root element missing from index.html');
}

const queryClient = createOpsQueryClient();

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <I18nProvider>
          <App />
        </I18nProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
